import { Component, OnInit, ViewChild, computed, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { selectCompanyName } from '../../../../../store/selectors/company-form.selectors';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { MessageDto, MessageAnswerDto } from '../../../../../models/api/messageDto';
import { MessageService } from '../../../../../services/message.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { ComposeMessageDialogComponent } from '../../../../messages/compose-message-dialog/compose-message-dialog.component';
import { LinkifyPipe } from '../../../../../pipes/linkify.pipe';
import { formatRecipients } from '../../../../../utilities/message-recipients';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

type Mailbox = 'inbox' | 'sent';

@Component({
  selector: 'app-messages-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    NgxSpinnerModule,
    DatePipe,
    RouterModule,
    LinkifyPipe
  ],
  templateUrl: './messages-tab.component.html',
  styleUrl: './messages-tab.component.scss'
})
export class MessagesTabComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private confirmationDialogService = inject(ConfirmationDialogService);
  private dialog = inject(MatDialog);
  private store = inject(Store);
  private currentUserState = inject(CurrentUserState);
  companyName = this.store.selectSignal(selectCompanyName);

  /**
   * Messages held read-only: composing, replying and deleting are hidden. Marking a message read is
   * left alone — that is the reader's own state, not a change to the conversation.
   *
   * Unlike the other tabs this is not backed by a server-side check: the message endpoints are shared
   * with the app-wide messaging UI, so gating them on a company-tab flag would also cut the user off
   * from messages outside the company form.
   */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Messages));

  dataSource = new MatTableDataSource<MessageDto>([]);
  totalItems = 0;
  pageSize = 5;
  pageNumber = 1;
  sortBy = 'CreatedDate';
  ascending = false;
  activeMailbox: Mailbox = 'inbox';
  companyId: string | null = null;

  // Inline message view
  selectedMessage: MessageDto | null = null;
  visibleAnswers: MessageAnswerDto[] = [];
  hasHiddenAnswers = false;
  isReplying = false;
  replyForm!: FormGroup;
  private allAnswers: MessageAnswerDto[] = [];

  readonly spinnerName = 'messages-tab-spinner';
  readonly viewSpinnerName = 'messages-tab-view-spinner';

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  get displayedColumns(): string[] {
    return this.activeMailbox === 'inbox'
      ? ['title', 'sentByUserFullName', 'createdDate', 'actions']
      : ['title', 'recipients', 'createdDate', 'actions'];
  }

  ngOnInit(): void {
    this.companyId = this.route.parent?.snapshot.paramMap.get('id')
      ?? this.route.snapshot.paramMap.get('id');
    this.replyForm = this.fb.group({
      answerText: ['', [Validators.required, Validators.maxLength(5000)]]
    });
    this.loadMessages();
  }

  onMailboxChange(mailbox: Mailbox): void {
    this.activeMailbox = mailbox;
    this.pageNumber = 1;
    this.dataSource.data = [];
    this.closeMessageView();
    this.loadMessages();
  }

  loadMessages(): void {
    this.spinnerService.show(this.spinnerName);
    const request$ = this.activeMailbox === 'inbox'
      ? this.messageService.getMessages(this.pageNumber, this.pageSize, this.sortBy, this.ascending, this.companyId ?? undefined)
      : this.messageService.getSentMessages(this.pageNumber, this.pageSize, this.sortBy, this.ascending, this.companyId ?? undefined);

    request$.pipe(
      map(response => {
        this.totalItems = response.totalCount;
        this.spinnerService.hide(this.spinnerName);
        return response.data;
      }),
      catchError(error => {
        console.error('Error loading messages:', error);
        this.spinnerService.hide(this.spinnerName);
        this.toast.error('Error loading messages', {
          iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
        });
        return of([]);
      })
    ).subscribe(messages => {
      this.dataSource.data = messages;
    });
  }

  onComposeMessage(): void {
    const dialogRef = this.dialog.open(ComposeMessageDialogComponent, {
      width: '600px',
      disableClose: true,
      data: { companyId: this.companyId, companyName: this.companyName() }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadMessages();
      }
    });
  }

  onSortChange(sort: Sort): void {
    if (sort.direction) {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
      this.loadMessages();
    }
  }

  onPageChange(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadMessages();
  }

  onRowClick(message: MessageDto): void {
    const messageId = message.isAnswer ? message.parentMessageId! : message.id;
    this.loadMessageView(messageId, message.isAnswer ? message.id : null, !message.isAnswer, message);
  }

  closeMessageView(): void {
    this.selectedMessage = null;
    this.visibleAnswers = [];
    this.allAnswers = [];
    this.hasHiddenAnswers = false;
    this.isReplying = false;
    this.replyForm.reset();
  }

  private loadMessageView(id: string, answerId: string | null, originalOnly: boolean, clickedMessage: MessageDto): void {
    this.spinnerService.show(this.viewSpinnerName);
    const markAsRead = this.activeMailbox !== 'sent';
    this.messageService.getMessage(id, markAsRead).subscribe({
      next: (message) => {
        this.selectedMessage = message;
        this.sortAnswers();
        this.applyAnswerFilter(answerId, originalOnly);
        this.spinnerService.hide(this.viewSpinnerName);
        if (markAsRead && !clickedMessage.isRead) {
          clickedMessage.isRead = true;
          this.messageService.refreshUnreadCount();
        }
      },
      error: (error) => {
        console.error('Error loading message:', error);
        this.spinnerService.hide(this.viewSpinnerName);
        this.toast.error('Error loading message', {
          iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
        });
      }
    });
  }

  onShowFullConversation(): void {
    this.visibleAnswers = [...this.allAnswers];
    this.hasHiddenAnswers = false;
  }

  onToggleReply(): void {
    this.isReplying = !this.isReplying;
    if (!this.isReplying) {
      this.replyForm.reset();
    }
  }

  onSendReply(): void {
    if (this.replyForm.invalid || !this.selectedMessage) {
      this.replyForm.markAllAsTouched();
      return;
    }

    this.spinnerService.show(this.viewSpinnerName);
    this.messageService.replyToMessage(this.selectedMessage.id, {
      answerText: this.replyForm.value.answerText
    }).subscribe({
      next: (answer) => {
        this.spinnerService.hide(this.viewSpinnerName);
        this.allAnswers = [...this.allAnswers, answer];
        this.visibleAnswers = [...this.allAnswers];
        this.hasHiddenAnswers = false;
        this.replyForm.reset();
        this.isReplying = false;
        this.toast.success('Reply sent successfully', {
          iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
        });
      },
      error: (error) => {
        this.spinnerService.hide(this.viewSpinnerName);
        this.toast.error('Error sending reply', {
          iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
        });
        console.error('Error sending reply:', error);
      }
    });
  }

  onDownloadAttachment(): void {
    if (!this.selectedMessage?.hasAttachment) return;

    this.messageService.getAttachment(this.selectedMessage.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.selectedMessage!.fileName || 'attachment';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading attachment:', error);
        this.toast.error('Error downloading attachment', {
          iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
        });
      }
    });
  }

  onToggleReadStatus(message: MessageDto, event: Event): void {
    event.stopPropagation();
    const newStatus = !message.isRead;
    this.messageService.updateReadStatus(message.id, { isRead: newStatus }).subscribe({
      next: () => {
        message.isRead = newStatus;
        this.messageService.unreadCount.update(count =>
          Math.max(0, newStatus ? count - 1 : count + 1)
        );
      },
      error: (error) => {
        console.error('Error updating read status:', error);
        this.toast.error('Error updating read status', {
          iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
        });
      }
    });
  }

  onDeleteClick(message: MessageDto, event: Event): void {
    event.stopPropagation();
    this.confirmationDialogService.openConfirmationDialog(
      'Delete Message',
      `Are you sure you want to delete the message "${message.title}"?`,
      'Delete',
      'Cancel'
    ).subscribe(confirmed => {
      if (confirmed) {
        this.spinnerService.show(this.spinnerName);
        this.messageService.deleteMessage(message.id).subscribe({
          next: () => {
            this.spinnerService.hide(this.spinnerName);
            this.toast.success('Message deleted successfully', {
              iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
            });
            if (this.selectedMessage?.id === message.id) {
              this.closeMessageView();
            }
            this.loadMessages();
          },
          error: (error) => {
            this.spinnerService.hide(this.spinnerName);
            this.toast.error('Error deleting message', {
              iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
            });
            console.error('Error deleting message:', error);
          }
        });
      }
    });
  }

  getRecipientNames(message: MessageDto): string {
    return formatRecipients(message.recipientUsers, message.recipientRoles);
  }

  getSelectedRecipientNames(): string {
    if (!this.selectedMessage) return '';
    return this.getRecipientNames(this.selectedMessage);
  }

  getAnswerRecipientNames(answer: MessageAnswerDto): string {
    return formatRecipients(answer.recipientUsers);
  }

  private sortAnswers(): void {
    if (this.selectedMessage?.answers) {
      this.selectedMessage.answers.sort((a, b) =>
        new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
      );
      this.allAnswers = [...this.selectedMessage.answers];
    }
  }

  private applyAnswerFilter(answerId: string | null, originalOnly: boolean): void {
    if (!this.selectedMessage) return;

    if (originalOnly) {
      this.visibleAnswers = [];
      this.hasHiddenAnswers = this.allAnswers.length > 0;
    } else if (answerId) {
      const index = this.allAnswers.findIndex(a => a.id === answerId);
      if (index !== -1) {
        this.visibleAnswers = this.allAnswers.slice(0, index + 1);
        this.hasHiddenAnswers = this.allAnswers.length > index + 1;
      } else {
        this.visibleAnswers = [...this.allAnswers];
        this.hasHiddenAnswers = false;
      }
    } else {
      this.visibleAnswers = [...this.allAnswers];
      this.hasHiddenAnswers = false;
    }
  }
}
